/**
 * README
 * This is an "internal" component meant to be used solely by other RC components.
 * Please don't use this directly. If it seems like you'd benefit from it please
 * reach out to #cadence for help coordinating!
 */
import React, { cloneElement } from 'react';
import classNames from 'classnames';
import {
  arrow,
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  size,
  safePolygon,
  useDelayGroup,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
  useClick,
  limitShift,
  useHover,
  useTransitionStatus
} from '@floating-ui/react';
import mergeRefs from 'react-merge-refs';
import { FLOATING_UI_DOM_ID, PANEL_DEFAULTS } from '@reverbdotcom/cadence/constants';

type FloatingUIPositions = 'bottom' | 'bottom-start' | 'bottom-end' | 'right';

interface RCNonModalDialogBaseProps {
  id: string,
  isOpen?: boolean,
  onOpenChange?: (open: boolean) => void,
  sticky?: boolean,
  position?: FloatingUIPositions,
  anchor: JSX.Element,
  children: React.ReactNode,
  /** Overrides base functionality to suit specific purposes
   * - Guide Popover: Adds arrow and delays animate in
   * - Legacy: temporary variant that does not automatically add a click handler to support legacy instances
   */
  variant?: 'default' | 'guide-popover' | 'legacy';
  dismissOnOutsidePress?: boolean;
  focusOrder?: Array<'reference' | 'floating' | 'content'>;
  trapFocus?: boolean;
  /** Index of the focusOrder array that should receive initial focus. Defaults to 0. Use -1 to prevent initial focus. */
  initialFocus?: -1 | 0 | 1 | 2;
  openOnHover?: boolean;
}

function ariaID(id) {
  return {
    label: `${id}-label`,
    description: `${id}-description`,
  };
}

function arrowSide(panelPlacement) {
  // the side of the anchor that the panel sits on comes from `placement`
  const [ panelSide ] = panelPlacement.split('-');

  // the arrow sits on that opposite side, so we remap it here.
  const arrowSideMapping = {
    top: 'bottom',
    right: 'left',
    bottom: 'top',
    left: 'right',
  };

  return arrowSideMapping[panelSide];
}

function arrowPositionStyles(arrowData, panelPlacement) {
  return {
    top: arrowData?.y && `${arrowData?.y}px`,
    left: `${arrowData?.x}px`,

    // override defaults above based on position
    [arrowSide(panelPlacement)]: `-${PANEL_DEFAULTS.arrowHeight}px`,
  };
}

export function RCNonModalDialogBase({
  id,
  isOpen: openProp = undefined,
  onOpenChange: setOpenProp = undefined,
  sticky = false,
  position = 'bottom-start',
  anchor,
  children,
  variant = 'default',
  dismissOnOutsidePress = true,
  focusOrder = ['content'],
  trapFocus = true,
  initialFocus = 0,
  openOnHover = false,
}: RCNonModalDialogBaseProps) {
  const arrowRef = React.useRef(null);
  const labelId = ariaID(id).label;
  const descriptionId = ariaID(id).description;

  const [openState, setOpenState] = React.useState<boolean>(openProp || false);

  const isControlled = openProp !== undefined && setOpenProp !== undefined;
  const open = isControlled ? openProp : openState;
  const setOpen = isControlled ? setOpenProp : setOpenState;

  function handleOpenChange(state: boolean) {
    setOpen(state);
    if (!isControlled) {
      setOpenProp?.(state);
    }
  }

  const isGuidePopover = variant === 'guide-popover';

  // Main config for libs "useFloating"
  const {
    x,
    y,
    refs,
    strategy,
    context,
    update,
    placement,
    middlewareData,
  } = useFloating({
    open,
    onOpenChange: handleOpenChange,
    strategy: 'fixed',
    middleware: [
      offset(PANEL_DEFAULTS.anchorOffset),
      flip(),
      shift({
        padding: {
          top: PANEL_DEFAULTS.arrowHeight,
          right: PANEL_DEFAULTS.windowEdgePadding,
          bottom: PANEL_DEFAULTS.windowEdgePadding,
          left: PANEL_DEFAULTS.windowEdgePadding,
        },
        mainAxis: true,
        crossAxis: true,
        limiter: sticky ? undefined : limitShift(),
      }),
      size({
        apply({ availableHeight, elements }) {
          const safeValue = Math.max(0, availableHeight);
          elements.floating.style.setProperty(
            '--available-height',
            `${safeValue / 10}rem`,
          );
        },
      }),
      arrow({
        element: arrowRef,
        padding: 16, // equal to border radius of popover panel
      }),
    ],
    placement: position,
    whileElementsMounted: (r, f, u) => {
      // We only need to auto update if it's possible to do so
      let cleanup = undefined;
      if (('ResizeObserver' in window)) {
        // animationFrame: true ensures the position stays correct when layout
        // shifts occur after initial render (e.g. ads or async content loading
        // above the reference element).
        cleanup = autoUpdate(r, f, u, { animationFrame: true });
      }
      return cleanup;
    },
  });

  // Prefer parent DelayGroup's delay values if available
  const { delay, currentId, isInstantPhase } = useDelayGroup(context);
  const transitionDelay = delay || {
    open: 350,
    close: 100,
  };

  const { isMounted, status } = useTransitionStatus(context, {
    duration: isInstantPhase ? {
      open: 0,
      close: currentId === context.floatingId ? 100 : 0,
    } : transitionDelay,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useRole(context, {
      enabled: isGuidePopover ? false : true,
      role: 'dialog', // this should be changed when this does more than a popover-like thing.
    }),
    useClick(context, {
      enabled: variant === 'default' ? true : false,
      toggle: true,
    }),
    useHover(context, {
      enabled: openOnHover,
      delay: transitionDelay,
      mouseOnly: true,
      // safePolygon uses `performance.now()` – only use it if available on the window
      handleClose: (typeof window !== 'undefined' && 'performance' in window) ? safePolygon() : undefined,
    }),
    useDismiss(context, {
      enabled: true,
      escapeKey: true,
      outsidePressEvent: 'mousedown',
      outsidePress: dismissOnOutsidePress,
    }),
  ]);

  // Preserve the consumer's ref
  const anchorRef = mergeRefs([refs.setReference, (anchor as any).ref]);

  const arrowCallback = React.useCallback(
    (node) => {
      arrowRef.current = node;
      update();
    },
    [update],
  );

  const classes = classNames(
    'rc-non-modal-dialog-base',
    { 'rc-non-modal-dialog-base--guide-popover': isGuidePopover },
  );

  if (anchor.type === React.Fragment) {
    // Fragments will render to the screen, but they lose their ref and the Popover UIs won't know what to anchor
    // themselves to, and the panel will end up in the top-left corner of the screen.
    throw new Error('Pass a single element to the `anchor` prop, Fragments or other arrays are not supported.');
  }

  return (
    <>
      {cloneElement(anchor, {
        ...getReferenceProps({ ref: anchorRef, ...anchor.props }),
      })}

      {isMounted && (
        <FloatingPortal id={FLOATING_UI_DOM_ID}>
          <FloatingFocusManager
            context={context}
            modal={trapFocus}
            order={focusOrder}
            returnFocus
            initialFocus={initialFocus}
            closeOnFocusOut={isGuidePopover ? false : true}
          >
            <div
              data-status={status}
              data-placement={placement}
              data-instant={isInstantPhase}
              ref={refs.setFloating}
              className={classes}
              style={{
                position: strategy,
                top: y ?? 0,
                left: x ?? 0,
              }}
              aria-labelledby={labelId}
              aria-describedby={descriptionId}
              role={isGuidePopover ? 'dialog' : undefined}
              {...getFloatingProps()}
            >
              {children}
              {isGuidePopover &&
                <div
                  className={`rc-non-modal-dialog-base__arrow rc-non-modal-dialog-base__arrow--${arrowSide(placement)}`}
                  ref={arrowCallback}
                  style={arrowPositionStyles(middlewareData?.arrow, placement)}
                />
              }
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
}
